import { z } from 'zod'
import { AdviesSetting } from './advies.js'
import { BELANG_CONTROLS, BelangenverklaringSetting } from './belangenverklaring.js'
import { DraftDefinitie } from './draft.js'
import { Block, DefinitieId, SectionId } from './id.js'
import { Moment, Person, compareMoments } from './moment.js'
import { Rule } from './rule.js'
import { WerkverzamelingSetting } from './answer.js'
import { Text } from './text.js'
import { TemplateOrigin } from './templateOrigin.js'
import { DOSSIER_CONTROLS,
  RESPONSE_SHAPE,
  type Control,
  MULTI_VALUED,
  type MultiValued,
  TEXT_CONTROLS,
  Field,
} from './field.js'

/**
 * De formulierdefinitie: één JSON-document (ontwerp 4.4).
 *
 * Secties zijn de dragende eenheid (R-01): de eenheid van uitleg, toegang,
 * opmerking, toets en herstel. Elke sectie heeft een type (D-04), een kolomkeuze
 * (R-11), een lijst velden en een lijst regels.
 *
 * De regels staan op de sectie en niet genest in het veld. Dat is het besluit
 * van deze milestone en het corrigeert het voorbeeld dat in ontwerp 4.4 stond:
 * een zichtbaarheidsregel kan een hele sectie als doel hebben (R-33), en dat past
 * niet in een veldeigenschap. Bijkomend voordeel: de projectie kan een sectie
 * weglaten en gooit dan meteen haar regels mee weg (R-01a, ontwerp 5.2).
 */

/**
 * Heeft een veld van dit controltype een compartiment nodig (D-69)?
 *
 * Afgeleid uit `RESPONSE_SHAPE` en niet als eigen lijst, en dat is een correctie. Er stond
 * hier `['verwijzing', 'subgrid', 'criterium', 'component']`, met de gedachte dat dat de
 * vier waren die niets aan het dossier leveren. Dat was een tweede definitie van iets wat
 * `veld.ts` al vastlegt, en hij was onvolledig: `status`, `notification`, `progress` en
 * `timer` staan daar op `geen` en zouden onder de oude lijst een compartiment hebben moeten
 * krijgen terwijl ze geen waarde dragen. Een review vond het.
 *
 * Twee antwoordvormen krijgen geen compartiment. `geen` levert niets aan het dossier: de vier
 * weergavetypen van de schil (B15), de alleen-lezen `verwijzing` en `subgrid` (R-56), en
 * de `component` die zijn eigen bijdrage meebrengt (R-46). En `laag` levert wel iets,
 * maar niet hier: de uitkomst van een criterium staat in het gestructureerde deel van het
 * contract (ontwerp 9.5) en reist langs de toetslaag.
 */
function hasCompartimentNeeded(control: Control): boolean {
  const shape = RESPONSE_SHAPE[control]
  return shape !== 'geen' && shape !== 'laag'
}

/**
 * Of dit veld een compartiment hoort te hebben (D-69, D-70).
 *
 * Geëxporteerd omdat er twee lezers zijn: de superRefine hieronder weigert een definitie
 * zonder, en het eigenschappenpaneel van de configurator biedt de keuze alleen aan waar hij
 * hoort (M5.10c). Zouden die twee de regel elk apart opschrijven, dan biedt het paneel een
 * compartiment aan waar het schema er geen wil, of laat het er een weg waar de poort er een eist.
 * Dezelfde reden waarom `referenceableBlocks` in de engine één plek heeft.
 *
 * Vier soorten hebben er geen, en dat is geen uitzondering maar de reden: een compartiment is een
 * plek in het document dat dit bouwblok oplevert, en wat niets oplevert heeft er geen. Een
 * bronsectie is in haar geheel alleen-lezen (R-56), en een rijveld landt in het compartiment van
 * zijn herhaalgroep en niet apart.
 */
export function needsCompartiment(
  control: Control,
  sectionType: 'gewoon' | 'bron' | 'toets',
  inRow: boolean,
  /** Een veld van het processysteem landt onder `<dossier>` en niet in een compartiment (R-202). */
  dossier = false,
): boolean {
  return sectionType !== 'bron' && !inRow && !dossier && hasCompartimentNeeded(control)
}

/** Het laag-eigenaarschap volgt uit het sectietype (R-172, D-56), niet uit een rollenmodel. */
export const SectionType = z.enum(['gewoon', 'bron', 'toets'])
export type SectionType = z.infer<typeof SectionType>

export const Section = z.strictObject({
  id: SectionId,
  type: SectionType,
  titel: Text,
  /** Uitleg boven de sectie (D-03). */
  uitleg: Text.optional(),
  kolommen: z.union([z.literal(1), z.literal(2)]),
  /**
   * De sectie waarover deze sectie een uitspraak doet (R-109, D-05). Een
   * toetssectie wijst de sectie aan die hij toetst; in een beoordelingsformulier
   * wijst een criteriumsectie zo naar zijn bronsectie. De koppeling zit op
   * sectieniveau en niet per vraag.
   */
  hoortBij: SectionId.optional(),
  velden: z.array(Field).min(1),
  regels: z.array(Rule),
})
export type Section = z.infer<typeof Section>

const Base = z.strictObject({
  id: DefinitieId,
  /**
   * Het bouwblok waarvan dit het formulier is (D-41).
   *
   * Zonder deze schakel weet een formulier niet bij welk bouwblok het hoort en een
   * verwijzing niet in welk contract ze wijst. Daar hangen drie dingen aan: het compartiment
   * waarin een zelf toegevoegd veld landt is een compartiment van het contract van dít blok
   * (D-69), publiceren geldt per bouwblok en per uitvraagmoment (D-67), en dupliceren
   * gebeurt juist om een startpunt voor een ánder bouwblok te maken (D-08).
   *
   * Het blok staat op de definitie en niet alleen op de publicatie, want het bepaalt
   * al tijdens het inrichten wat het formulier mag zeggen en waar zijn uitkomst heen
   * gaat. Het is dus geen context maar vorm.
   */
  blok: Block,
  /**
   * Of en hoe in dit formulier advies te vragen is (besluit van 30 oktober 2026, R-184, ontwerp 7.4a).
   *
   * Optioneel, en ontbreken is uit: een definitie van vóór dat besluit houdt haar gedrag, en er is geen
   * permissieve standaard. Het bevriest met de publicatie (D-36); een noodschakelaar per publicatie in
   * de opslag kan het alleen uitzetten. Het is geen rollenmodel (U6): het zegt niet wie wat ziet, alleen
   * of de functie in dit formulier aan staat en welke secties er buiten blijven.
   */
  advies: AdviesSetting.optional(),
  /**
   * Welke velden de partijen noemen die een adviseur ziet vóór zijn belangenverklaring: de titel, de
   * aanvrager en de mede-aanvragers (besluit van de gebruiker van 30 oktober 2026, R-184h, ontwerp
   * 7.4a). Optioneel, en ontbreken is geen koppeling: dan noemt de verklaring niemand. Een eigen
   * instelling naast `advies`, want de control is herbruikbaar buiten advies.
   */
  belangenverklaring: BelangenverklaringSetting.optional(),
  titel: Text,
  /**
   * Wat de ontvanger bij een overdracht standaard te zien krijgt (D-59, R-176).
   *
   * Optioneel en partieel: wat er niet in staat volgt de standaardregel uit het eisenboek. Het
   * staat op de definitie en niet op de publicatie, want het is vorm en geen uitvoering: de
   * inrichter stelt het in terwijl hij het formulier maakt, en het bevriest mee (R-99).
   *
   * Welke overdrachten er binnen het bouwblok zijn en wie er aan de beurt is, blijft een
   * instelling van het bouwblok en niet van het formulier (D-60). Wat hier staat is alleen wat
   * het formulier bij zo'n overdracht laat zien.
   */
  werkverzameling: WerkverzamelingSetting.optional(),
  /**
   * Of het formulier na indienen wordt getoetst: "Toetsen na indienen: Nee / Ja" (besluit van 2
   * november 2026, D-55, D-56).
   *
   * Optioneel, en dat is de achterwaartse compatibiliteit: ontbreekt het, dan volgt het uit de
   * secties (`heeftToetslaag`), dus een definitie van vóór die dag houdt haar gedrag. De toets
   * blijft een laag in dezelfde definitie (U19, R-171); deze vlag zegt alleen dat het ontbreken
   * van die laag een keuze is en geen vergetelheid. Tijdens het inrichten mogen vlag en secties
   * verschillen (eerst kiezen, dan bouwen); de poortcontrole `toets` houdt ze bij publiceren gelijk,
   * zodat een publicatie nooit een vlag draagt die haar secties tegenspreekt.
   *
   * Of een toets die er is ook echt wordt uitgezet, blijft van het processysteem (D-60).
   */
  getoetst: z.boolean().optional(),
  /**
   * Of de vragen per sectie zijn genummerd (R-04, besluit van 30 oktober 2026). Afwezig is aan, zodat een
   * definitie van vóór die dag nummert zoals ze deed; de inrichter zet het per formulier uit. Als er wordt
   * genummerd, dan per sectie en niet doorlopend (R-04).
   */
  nummering: z.boolean().optional(),
  /**
   * Uit welk sjabloon en welke versie dit formulier is gemaakt (D-34, besluit van 1 november 2026).
   * Herkomst en nooit vorm; zie `TemplateOrigin`. Het bevriest mee met de publicatie.
   */
  uitSjabloon: TemplateOrigin.optional(),
  secties: z.array(Section).min(1),
})

/**
 * De definitie met de controles die over secties heen gaan.
 *
 * Deze controles zijn `superRefine` en overleven de JSON Schema-generatie niet
 * (opdracht 7): het gegenereerde schema toetst de vorm, dit schema toetst
 * bovendien de samenhang. Wie een definitie via JSON Schema valideert heeft dus
 * niet dezelfde zekerheid als wie hem hier door haalt. Dat staat zo in de
 * README van het pakket.
 *
 * Wat hier niet wordt getoetst: een kring in de zichtbaarheidsregels. Die weert
 * de configurator al bij het vastleggen (D-21, M5), en de evaluator rekent door
 * tot de uitkomst stabiel is (ontwerp 5.6).
 */
/**
 * De samenhang van de koppeling voor de belangenverklaring (R-184h).
 *
 * Elk aangewezen veld bestaat, staat op sectieniveau in een gewone sectie, en is platte tekst
 * (`BELANG_CONTROLS`): een toetssectie is van de toetslaag en een bronsectie draagt geen antwoord in de
 * stand. Een sectie die is uitgesloten van advies levert ook geen partij: anders zag een adviseur toch
 * iets uit die sectie. De kolommen van de mede-aanvragers zijn rijvelden van de aangewezen herhaalgroep.
 */
function checkBelangenverklaring(definitie: z.infer<typeof Base>, ctx: z.RefinementCtx): void {
  const setting = definitie.belangenverklaring
  if (setting === undefined) return
  const excluded = new Set(definitie.advies?.uitgeslotenSecties ?? [])
  const located = new Map<string, { readonly veld: Field; readonly sectie: Section }>()
  for (const section of definitie.secties) for (const field of section.velden) located.set(field.id, { veld: field, sectie: section })
  const textual = (control: string): boolean => (BELANG_CONTROLS as readonly string[]).includes(control)

  /** Het veld bij zijn label, zoals de inrichter het kent; de id alleen als er geen label is. */
  const named = (field: Field): string => {
    const label = 'label' in field ? field.label?.nl : undefined
    return label === undefined || label.trim() === '' ? `veld ${field.id}` : `"${label}"`
  }

  function place(fieldId: string, path: (string | number)[], what: 'tekst' | 'groep'): Field | undefined {
    const found = located.get(fieldId)
    const issue = (message: string) => ctx.addIssue({ code: 'custom', path: ['belangenverklaring', ...path], message })
    if (found === undefined) {
      issue('de belangenverklaring noemt een veld dat niet (meer) op sectieniveau in het formulier staat; kies het opnieuw bij Partijen in de belangenverklaring')
      return undefined
    }
    const name = named(found.veld)
    if (found.sectie.type !== 'gewoon') issue(`de belangenverklaring noemt ${name} in een ${found.sectie.type}sectie; een partij komt uit een gewone sectie`)
    if (excluded.has(found.sectie.id)) issue(`de belangenverklaring noemt ${name} in een sectie die is uitgesloten van advies`)
    if (what === 'tekst' && !textual(found.veld.control)) issue(`de belangenverklaring noemt ${name}, en dat is geen tekstveld`)
    if (what === 'groep' && found.veld.control !== 'herhaalgroep') issue(`de belangenverklaring noemt ${name} voor de mede-aanvragers, en dat is geen herhaalgroep`)
    return found.veld
  }

  if (setting.titel !== undefined) place(setting.titel, ['titel'], 'tekst')
  if (setting.aanvrager !== undefined) {
    place(setting.aanvrager.naam, ['aanvrager', 'naam'], 'tekst')
    if (setting.aanvrager.organisatie !== undefined) place(setting.aanvrager.organisatie, ['aanvrager', 'organisatie'], 'tekst')
  }
  const group = setting.medeAanvragers
  if (group !== undefined) {
    const field = place(group.groep, ['medeAanvragers', 'groep'], 'groep')
    if (field?.control === 'herhaalgroep') {
      const columns: [string | undefined, string][] = [
        [group.naam, 'naam'],
        [group.organisatie, 'organisatie'],
      ]
      for (const [columnId, key] of columns) {
        if (columnId === undefined) continue
        const column = field.rijVelden.find((rowField) => rowField.id === columnId)
        if (column === undefined || !textual(column.control)) {
          ctx.addIssue({
            code: 'custom',
            path: ['belangenverklaring', 'medeAanvragers', key],
            message: `de kolom voor de ${key} van de mede-aanvragers is geen tekstkolom in de rij van ${named(field)}`,
          })
        }
      }
    }
  }
}

export const Definitie = Base.superRefine((definitie, ctx) => {
  const sectionIds = new Set(definitie.secties.map((s) => s.id))
  // Een uitgesloten sectie bestaat; anders sluit de instelling iets uit dat er niet is, en valt een
  // hernoemde sectie stil buiten de uitsluiting.
  definitie.advies?.uitgeslotenSecties.forEach((sectionId, index) => {
    if (!sectionIds.has(sectionId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['advies', 'uitgeslotenSecties', index],
        message: `sectie ${sectionId} staat niet in deze definitie`,
      })
    }
  })
  checkBelangenverklaring(definitie, ctx)
  const criterionIds = new Map<string, number>()
  const allFieldIds = new Set<string>()
  const seen = new Map<string, string>()
  /** Per naam van een veld van het processysteem het formulierveld dat hem toont. */
  const dossierNames = new Map<string, string>()

  function oneTime(id: string, where: string, path: (string | number)[]): void {
    const earlier = seen.get(id)
    if (earlier !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: path,
        // Een id wijzigt nooit en wordt nooit hergebruikt (ontwerp 4.3). Twee
        // dingen met hetzelfde id koppelen antwoorden aan de verkeerde vraag.
        message: `id ${id} komt twee keer voor: ${earlier} en ${where}`,
      })
    } else {
      seen.set(id, where)
    }
  }

  oneTime(definitie.id, 'de definitie', ['id'])

  // Eerste ronde: alle ids innemen, zodat een bronveld naar een latere sectie mag wijzen.
  definitie.secties.forEach((section, s) => {
    oneTime(section.id, `sectie ${section.id}`, ['secties', s, 'id'])
    fields(section).forEach(({ veld: field, pad: path }) => {
      allFieldIds.add(field.id)
      oneTime(field.id, `veld ${field.id}`, ['secties', s, ...path, 'id'])
      if (field.control === 'criterium') {
        criterionIds.set(field.criteriumId, (criterionIds.get(field.criteriumId) ?? 0) + 1)
      }
    })
    section.regels.forEach((rule, r) => {
      oneTime(rule.id, `regel ${rule.id}`, ['secties', s, 'regels', r, 'id'])
    })
  })

  definitie.secties.forEach((section, s) => {
    const ownFields = new Map(fields(section).map(({ veld: field }) => [field.id, field]))
    const ownFieldIds = new Set(ownFields.keys())
    const path = (rest: (string | number)[]) => ['secties', s, ...rest]

    if (section.hoortBij !== undefined) {
      if (section.type === 'bron') {
        ctx.addIssue({
          code: 'custom',
          path: path(['hoortBij']),
          // Een bronsectie is de inhoud waar het over gaat en doet zelf geen uitspraak.
          message: 'een bronsectie heeft geen hoortBij',
        })
      }
      if (section.hoortBij === section.id) {
        ctx.addIssue({ code: 'custom', path: path(['hoortBij']), message: 'een sectie hoort niet bij zichzelf' })
      } else if (!sectionIds.has(section.hoortBij)) {
        ctx.addIssue({
          code: 'custom',
          path: path(['hoortBij']),
          message: `hoortBij wijst naar ${section.hoortBij}, en die sectie bestaat niet`,
        })
      }
    }

    // Een bronsectie is de inhoud waar het over gaat en wordt door niemand
    // geschreven (R-56, D-04, ontwerp 4.4 en 8.3). De toets is dus niet welk
    // controltype er staat maar of het veld een bron heeft: een verwijzing naar
    // één waarde, of een alleen-lezen tabel over een pad naar een sectie of een
    // lijst (R-55), zoals de oordelen uit eerdere beoordelingen onder elkaar
    // (R-62). Zonder bron zou er invulbare inhoud in een alleen-lezen sectie
    // staan, en dat is precies wat het laag-eigenaarschap uitsluit.
    if (section.type === 'bron') {
      section.velden.forEach((field, v) => {
        const hasSource = 'bron' in field && field.bron !== undefined
        if (!hasSource) {
          ctx.addIssue({
            code: 'custom',
            path: path(['velden', v]),
            // In de woorden van de inrichter, want de publicatiepoort toont deze melding bij het veld (besluit van 4
            // november 2026): een verwijzing of tabel zonder pad mist dat pad, al het andere hoort er niet.
            message:
              field.control === 'verwijzing' || field.control === 'subgrid'
                ? 'dit veld in een bronsectie heeft nog geen pad uit het dossier; kies het pad waaruit het leest'
                : 'een bronsectie toont alleen velden uit het dossier; zet dit veld in een gewone sectie',
          })
        }
      })
    }

    /**
     * Een teruglegopmerking staat in de toetslaag en draagt tekst (R-75).
     *
     * Twee grenzen op één instelling. Buiten een toetssectie heeft `gaatTerug` geen
     * betekenis: de invoerlaag is al van de invuller en hoeft niets terug te krijgen. En op
     * een cijfer of een keuze heeft het geen betekenis: wat de invuller bij zijn antwoord
     * ziet is een opmerking en geen oordeel, want het oordeel reist langs zijn eigen weg
     * (R-182, R-187). Beide worden hier geweigerd in plaats van bij het lezen genegeerd: een
     * instelling die stil niets doet is erger dan een die niet wordt aangenomen.
     */
    /**
     * Over alle velden en niet alleen de bovenste laag, en dat is een correctie.
     *
     * Dit liep eerst over `sectie.velden`, dus een rijveld van een herhaalgroep met
     * `gaatTerug: true` kwam er ongemoeid door en deed daarna niets. Dat is precies wat de
     * alinea hierboven verbiedt: een instelling die stil niets doet is erger dan een die
     * niet wordt aangenomen. Een review vond het.
     */
    fields(section).forEach(({ veld: field, pad: fieldPath }) => {
      // `in` en geen directe eigenschap: een component draagt alleen zijn kern en heeft dit
      // veld niet, net zoals bij `bron` hierboven.
      if (!('gaatTerug' in field) || field.gaatTerug !== true) return
      if (section.type !== 'toets') {
        ctx.addIssue({
          code: 'custom',
          path: path([...fieldPath, 'gaatTerug']),
          message: 'alleen een veld in een toetssectie gaat terug naar de indiener',
        })
      }
      if (!(TEXT_CONTROLS as readonly string[]).includes(field.control)) {
        ctx.addIssue({
          code: 'custom',
          path: path([...fieldPath, 'gaatTerug']),
          message: `een teruglegopmerking draagt tekst; ${field.control} draagt er geen`,
        })
      }
    })

    /**
     * Het compartiment (D-69, D-70): elk veld dat een antwoord oplevert heeft er precies één.
     *
     * **Wat "een veld dat de inrichter zelf toevoegt" hier betekent.** D-69 zegt het zo,
     * en in dit model is dat elk antwoordveld: de veldcatalogus levert controltypen en
     * geen voorgecontracteerde velden, dus wat er in een formulier staat is vanuit het
     * contract gezien altijd zelf toegevoegd. Het gestructureerde deel van een contract
     * is geschreven door wie het bouwblok bouwde en niet door de inrichter.
     *
     * **Vier soorten hebben er geen, en dat is geen uitzondering maar de reden.** Een
     * compartiment is een plek in het document dat dit bouwblok oplevert; wat niets oplevert
     * heeft er geen. Een bronsectie is in haar geheel alleen-lezen (R-56), een
     * `verwijzing` en een `subgrid` lezen uit het dossier, een `criterium` levert zijn
     * uitkomst in het gestructureerde deel (ontwerp 9.5), en een `component` brengt zijn
     * eigen bijdrage mee (R-46). Een rijveld van een herhaalgroep landt in het compartiment van
     * die groep en niet apart: de groep is één waarde, een lijst rijen.
     *
     * Ontwerp 8.11 zegt alleen "op de velddefinitie komt een verplichte verwijzing naar
     * een compartiment-identiteit" en noemt die vier niet. Dit is dus een lezing en geen citaat;
     * hij staat als melding in `docs/dekking.md`.
     */
    fields(section).forEach(({ veld: field, pad: fieldPath }) => {
      const inRow = fieldPath.includes('rijVelden')
      const dossier = 'dossierveld' in field && field.dossierveld !== undefined
      const needsOne = needsCompartiment(field.control, section.type, inRow, dossier)
      const hasCompartiment = 'compartiment' in field && field.compartiment !== undefined
      if (needsOne && !hasCompartiment) {
        ctx.addIssue({
          code: 'custom',
          path: path([...fieldPath, 'compartiment']),
          // Geen standaard in het schema: een bevroren definitie draagt haar compartiment uitdrukkelijk. De
          // standaard (het compartiment dat niet doorgeeft, besluit van 4 november 2026) zetten het werkblad
          // en de poort, uit het contract van het blok; dat kent dit schema niet.
          message: `veld ${field.id} heeft geen compartiment in het contract van blok ${definitie.blok}`,
        })
      }
      if (!needsOne && hasCompartiment) {
        ctx.addIssue({
          code: 'custom',
          path: path([...fieldPath, 'compartiment']),
          message: inRow
            ? `een rijveld landt in het compartiment van zijn herhaalgroep, niet apart`
            : section.type === 'bron'
              ? `een bronsectie is alleen-lezen, dus ${field.control} heeft daar geen compartiment`
              : dossier
                ? `veld ${field.id} toont een veld van het processysteem en landt onder dossier, niet in een compartiment`
                : `${field.control} heeft antwoordvorm ${RESPONSE_SHAPE[field.control]} en levert dus niets in een compartiment`,
        })
      }
    })

    /**
     * Het akkoord per persoon alleen in een gewone sectie (eisenboek 12.56): de kring zijn de invullers,
     * en die schrijven in de invoerlaag. Een toets- of bronsectie heeft geen invullers. Alleen een nieuwe
     * vorm, dus geen bewaarde publicatie die hierdoor niet meer leest (D-36).
     */
    section.velden.forEach((field, f) => {
      if (field.control === 'akkoordPerPersoon' && section.type !== 'gewoon') {
        ctx.addIssue({ code: 'custom', path: path(['velden', f]), message: `een akkoord per persoon staat in een gewone sectie, niet in een ${section.type}sectie` })
      }
    })

    /**
     * Een veld van het processysteem (R-54, R-56, U11): alleen op een waardeveld, alleen in een
     * gewone sectie van de invoer, niet in een rij van een herhaalgroep, en elke naam één keer. Het
     * bericht draagt het onder `<dossier>` op die naam (R-202), dus twee velden met dezelfde naam
     * zouden elkaar overschrijven.
     */
    fields(section).forEach(({ veld: field, pad: fieldPath }) => {
      if (!('dossierveld' in field) || field.dossierveld === undefined) return
      const where = path([...fieldPath, 'dossierveld'])
      if (!(DOSSIER_CONTROLS as readonly string[]).includes(field.control)) {
        ctx.addIssue({ code: 'custom', path: where, message: `${field.control} kan geen veld van het processysteem tonen; alleen ${DOSSIER_CONTROLS.join(', ')}` })
      }
      if (fieldPath.includes('rijVelden')) {
        ctx.addIssue({ code: 'custom', path: where, message: `een rijveld kan geen veld van het processysteem tonen` })
      }
      if (section.type !== 'gewoon') {
        ctx.addIssue({ code: 'custom', path: where, message: `een veld van het processysteem staat in een gewone sectie, niet in een ${section.type}sectie` })
      }
      const naam = field.dossierveld.naam
      if (dossierNames.has(naam)) {
        ctx.addIssue({ code: 'custom', path: where, message: `veld ${naam} van het processysteem staat er al bij veld ${dossierNames.get(naam) ?? ''}; één formulierveld per veld` })
      } else {
        dossierNames.set(naam, field.id)
      }
    })

    /**
     * Een criterium dat aan een veld hangt, hangt aan een veld dat bestaat (D-55).
     *
     * Het veld mag in een andere sectie staan, en dat is de bedoeling: een toetscriterium doet
     * een uitspraak over een vraag van het formulier dat wordt getoetst, en die staat per
     * definitie niet in de toetssectie. Naar zichzelf wijzen mag niet: dan zou een criterium
     * zijn eigen onderwerp zijn en zou de werkverzameling naar de toetssectie verwijzen in
     * plaats van naar de vraag.
     */
    fields(section).forEach(({ veld: field, pad: fieldPath }) => {
      if (field.control !== 'criterium' || field.hoortBijVeld === undefined) return
      if (field.hoortBijVeld === field.id) {
        ctx.addIssue({
          code: 'custom',
          path: path([...fieldPath, 'hoortBijVeld']),
          message: 'een criterium hoort niet bij zichzelf',
        })
      } else if (!allFieldIds.has(field.hoortBijVeld)) {
        ctx.addIssue({
          code: 'custom',
          path: path([...fieldPath, 'hoortBijVeld']),
          message: `hoortBijVeld ${field.hoortBijVeld} bestaat niet in deze definitie`,
        })
      }
    })

    section.regels.forEach((rule, r) => {
      const here = (key: string) => path(['regels', r, key])
      if (rule.soort === 'zichtbaarBij') {
        if (rule.doel !== section.id && !ownFieldIds.has(rule.doel)) {
          ctx.addIssue({
            code: 'custom',
            path: here('doel'),
            // De regel hoort bij wat hij beheerst, zodat de projectie hem met de sectie meeneemt.
            message: `doel ${rule.doel} is niet deze sectie en geen veld erin`,
          })
        }
        // Het bronveld mag juist elders staan: R-33 zegt "een antwoord elders in het formulier".
        if (!allFieldIds.has(rule.bronVeld)) {
          ctx.addIssue({
            code: 'custom',
            path: here('bronVeld'),
            message: `bronVeld ${rule.bronVeld} bestaat niet in deze definitie`,
          })
        }
      } else if (rule.soort === 'zichtbaarAls') {
        // Dezelfde twee eisen als bij `zichtbaarBij`, per voorwaarde (12.31).
        if (rule.doel !== section.id && !ownFieldIds.has(rule.doel)) {
          ctx.addIssue({
            code: 'custom',
            path: here('doel'),
            message: `doel ${rule.doel} is niet deze sectie en geen veld erin`,
          })
        }
        rule.voorwaarden.forEach((condition, c) => {
          if (!allFieldIds.has(condition.bronVeld)) {
            ctx.addIssue({
              code: 'custom',
              path: path(['regels', r, 'voorwaarden', c, 'bronVeld']),
              message: `bronVeld ${condition.bronVeld} bestaat niet in deze definitie`,
            })
          }
        })
      } else if (rule.soort === 'korteSectie') {
        if (rule.doelSectie !== section.id) {
          ctx.addIssue({
            code: 'custom',
            path: here('doelSectie'),
            message: `doelSectie ${rule.doelSectie} is niet de sectie waarop deze regel staat`,
          })
        }
      } else if (!ownFieldIds.has(rule.doelVeld)) {
        ctx.addIssue({
          code: 'custom',
          path: here('doelVeld'),
          message: `doelVeld ${rule.doelVeld} staat niet in deze sectie`,
        })
      } else if (rule.soort === 'minimumAantal' || rule.soort === 'maximumAantal') {
        // Een aantal begrenzen kan alleen op een veld waarvan de waarde een
        // verzameling is (R-42). Op een tekstveld zou de regel niets betekenen en
        // toch indienen blokkeren.
        const target = ownFields.get(rule.doelVeld)
        if (target !== undefined && !MULTI_VALUED.includes(target.control as MultiValued)) {
          ctx.addIssue({
            code: 'custom',
            path: here('doelVeld'),
            message: `${rule.soort} werkt op ${MULTI_VALUED.join(', ')}, niet op ${target.control}`,
          })
        }
        // Een checkbox zonder opties is één losse instemming en heeft geen aantal.
        if (target !== undefined && target.control === 'checkbox' && target.opties === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: here('doelVeld'),
            message: 'een checkbox zonder opties heeft geen aantal om te begrenzen',
          })
        }
      } else if (rule.soort === 'geheelGetal') {
        // Alleen op een getalveld (besluit van 27 oktober 2026). Op een tekstveld zou de regel elke
        // invoer weigeren, want tekst is nooit een getal, en dan blokkeert hij indienen zonder dat de
        // invuller kan zien waarom.
        const target = ownFields.get(rule.doelVeld)
        if (target !== undefined && target.control !== 'number') {
          ctx.addIssue({
            code: 'custom',
            path: here('doelVeld'),
            message: `geheelGetal werkt op een number-veld, niet op ${target.control}`,
          })
        }
      }
    })
  })

  for (const [criterionId, count] of criterionIds) {
    if (count > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['secties'],
        // Aan dit id hangen weging, ranking en aggregatie buiten het product (R-150, R-151).
        message: `criteriumId ${criterionId} komt ${count} keer voor`,
      })
    }
  }
})

export type Definitie = z.infer<typeof Definitie>

/**
 * Of dit formulier een toetslaag heeft: de instelling `getoetst`, en zonder instelling of er een
 * toetssectie is (besluit van 30 oktober 2026).
 *
 * De enige plek waar die afleiding staat. De poort, de projectie, het bericht aan het processysteem,
 * de weigering van een toetsbeurt en de configurator lezen het hier, zodat ze het nooit oneens zijn.
 * Het type is smal met opzet: een concept en een bevroren definitie gaan er allebei doorheen.
 */
export function heeftToetslaag(definitie: {
  readonly getoetst?: boolean | undefined
  readonly secties: readonly { readonly type: SectionType }[]
}): boolean {
  return definitie.getoetst ?? definitie.secties.some((section) => section.type === 'toets')
}

/** Alle velden van een sectie, inclusief de rijvelden van een herhaalgroep. */
export function fields(section: Section): { veld: Field; pad: (string | number)[] }[] {
  const out: { veld: Field; pad: (string | number)[] }[] = []
  section.velden.forEach((field, v) => {
    out.push({ veld: field, pad: ['velden', v] })
    if (field.control === 'herhaalgroep') {
      field.rijVelden.forEach((rowField, r) => {
        out.push({ veld: rowField, pad: ['velden', v, 'rijVelden', r] })
      })
    }
  })
  return out
}

/**
 * De designtime-kant van een definitie: het concept waaraan de inrichter werkt,
 * met de historie eraan. Dit is `form_definition` uit ontwerp 4.2.
 *
 * De naam draagt "concept" met een voorvoegsel omdat het woord in dit product
 * twee dingen betekent: hier een definitie die nog niet is gepubliceerd, en in
 * eisenboek 8.10 een formulier dat nog niet is ingediend. Dat zijn verschillende
 * dingen aan verschillende kanten van de streep uit ontwerp 4.1.
 *
 * **De historie zegt wie en wanneer, niet wat.** Dat is geen bezuiniging maar een
 * besluit: de definitie-diff is Uit (D-32, ontwerp 13.4), dus er is geen model dat
 * beschrijft wat er tussen twee standen is veranderd, en eerdere standen worden
 * niet bewaard. Wat D-45 vraagt is precies dit: elke wijziging herleidbaar naar
 * persoon en moment. Wie hier later een diff bij wil, verandert daarmee D-32 en
 * niet alleen dit schema.
 *
 * Wat er ook niet in staat: of dit concept al is gepubliceerd. Dat volgt uit het
 * bestaan van een `Publicatie` met dit `definitieId`, en een tweede plek waar het
 * staat kan daarmee in tegenspraak komen. Bij publicatie gaat de vorm op slot
 * (R-99) en een volgend uitvraagmoment is een duplicaat met een eigen id
 * (R-102, D-08), dus deze reeks loopt niet door over een publicatie heen.
 */

/**
 * Eén vastlegging: iemand heeft op een moment een stand opgeslagen (D-45).
 *
 * **`z.object` en niet `z.strictObject`, en dat is een besluit van 28 oktober 2026.** Een onbekende
 * sleutel wordt weggelaten in plaats van geweigerd. Zo kan een oudere configurator of store een
 * historie blijven lezen waar een latere versie een sleutel aan heeft toegevoegd, zoals `automatisch`
 * (D-74); met een strikt schema valt na een terugrol elke definitie met zo'n regel om. Een bekende
 * sleutel met een verkeerde waarde blijft een fout. Een terugrol naar een versie van vóór die datum
 * leest `automatisch` nog strikt; zie `docs/dekking.md` voor de eenmalige opruiming die dat vraagt.
 */
export const DefinitieChange = z.object({
  /** Oplopend vanaf 1, zodat de reeks een ordening heeft die niet van het moment afhangt. */
  volgnummer: z.number().int().positive(),
  persoon: Person,
  moment: Moment,
  /**
   * Of deze stand automatisch is opgeslagen en niet met de knop Opslaan (D-74, G048).
   *
   * Optioneel, en alleen aanwezig als hij `true` is: een vastlegging met de knop, en elke
   * vastlegging van vóór 28 oktober 2026, draagt hem niet. Zo blijft de vorm van elke bestaande
   * historieregel precies wat hij was, en is de toevoeging aan de store-API additief (ontwerp 3.5).
   * Een automatische stand is een gewone stand met een eigen volgnummer; de vlag is er zodat een
   * overzicht van de historie de automatische standen kan wegfilteren.
   */
  automatisch: z.literal(true).optional(),
})
export type DefinitieChange = z.infer<typeof DefinitieChange>

export const DefinitieDraft = z
  .strictObject({
    id: DefinitieId,
    /**
     * De stand waaraan nu wordt gewerkt, in de losse conceptvorm: een formulier dat
     * nog niet af is moet op te slaan zijn (ontwerp 8.1 punt 2). `verhard` maakt er
     * een strikte `Definitie` van, en dat gebeurt pas bij het publiceren.
     *
     * Eerdere standen worden niet bewaard.
     */
    stand: DraftDefinitie,
    /**
     * Append-only, oudste eerst. Minstens één vastlegging, want een concept
     * ontstaat door een handeling van iemand; de laatste vastlegging beschrijft de
     * huidige stand.
     */
    historie: z.array(DefinitieChange).min(1),
  })
  .superRefine((draft, ctx) => {
    if (draft.stand.id !== draft.id) {
      ctx.addIssue({
        code: 'custom',
        path: ['stand', 'id'],
        // Eén identiteit voor dit formulier, van concept tot publicatie: de
        // publicatie verwijst met datzelfde id terug (ontwerp 4.2).
        message: `de stand draagt id ${draft.stand.id} en het concept ${draft.id}`,
      })
    }
    draft.historie.forEach((change, i) => {
      if (change.volgnummer !== i + 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['historie', i, 'volgnummer'],
          message: `verwacht volgnummer ${i + 1}, niet ${change.volgnummer}`,
        })
      }
      const previous = draft.historie[i - 1]
      // Op de tijdwaarde en niet op de tekst: `2026-01-01T00:00:00.500Z` sorteert als
      // string vóór `2026-01-01T00:00:00Z`, want een punt komt voor een Z. `revisies.ts`
      // schreef die valkuil al op; hier stond hij nog wel. Het gevolg was erger dan een
      // verkeerde melding: `toMoment` kort een hele seconde af tot `...:00Z`, dus viel
      // een schrijfactie op milliseconde nul en de volgende in dezelfde seconde, dan
      // weigerde dit schema een volstrekt geldige historie, rolde de transactie terug en
      // was het werk van de inrichter weg. Ongeveer één op de duizend schrijfacties.
      // Omgekeerd glipte een echt teruglopend moment er juist door. Een review vond het.
      if (previous !== undefined && compareMoments(change.moment, previous.moment) < 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['historie', i, 'moment'],
          // Een historie die terugloopt maakt "wie was de laatste" onbeslisbaar.
          message: `${change.moment} ligt voor de vorige vastlegging ${previous.moment}`,
        })
      }
    })
  })

export type DefinitieDraft = z.infer<typeof DefinitieDraft>
